import { addDays, format, getISOWeek, isAfter, isBefore } from 'date-fns';
import { dbDayDateToken } from '../../static/dbDayDateToken';
import { AvailabilityListUser } from '../models/availabilityListUser';
import { AvailabilityStatusEnum } from '../models/availabilityStatusEnum';
import { ShiftEnum } from '../models/shift.enum';
import { PoolerUser } from '../models/user';
import { UserAvailability } from '../models/userAvailability';
import {
  UserRestWarning,
  UserRestWarningTypeEnum,
} from '../models/userRestWarning';

export type MonthGroup = {
  monthIndex: number;
  days: GroupedDay[];
};

export type WeekGroup = {
  isoWeek: number;
  days: GroupedDay[];
};
export type GroupedDay = {
  date: string;
  totalIndex: number;
};

export class CalendarTableHelper {
  start: Date;
  end: Date;
  days: string[];
  months: MonthGroup[];
  weeks: WeekGroup[];
  breakFrom?: Date;
  breakTo?: Date;

  constructor(start: Date, end: Date, breakFrom?: Date, breakTo?: Date) {
    this.start = start;
    this.end = end;
    this.breakFrom = breakFrom;
    this.breakTo = breakTo;
    this.days = this.getDays(start, end);
    this.weeks = this.getWeeks(this.days);
    this.months = this.getMonths(this.days);
  }

  getDays(start: Date, end: Date): string[] {
    const days: string[] = [];
    let loopDate = start;
    while (end.getTime() >= loopDate.getTime()) {
      if (
        !this.breakFrom ||
        !this.breakTo ||
        !(isAfter(loopDate, this.breakFrom) && isBefore(loopDate, this.breakTo))
      ) {
        days.push(format(loopDate, dbDayDateToken));
      }
      loopDate = addDays(loopDate, 1);
    }
    return days;
  }

  getMonths(days: string[]) {
    const months: MonthGroup[] = [];
    days.forEach((day, index) => {
      const date = new Date(day);
      const preparedDay = {
        date: day,
        totalIndex: index,
      };
      let existingMonthGroup = months.find(
        (month) => month.monthIndex === date.getMonth(),
      );
      if (!existingMonthGroup) {
        existingMonthGroup = {
          monthIndex: date.getMonth(),
          days: [],
        };
        months.push(existingMonthGroup);
      }
      existingMonthGroup.days.push(preparedDay);
    });
    return months;
  }

  getWeeks(days: string[]) {
    const weekGroups: WeekGroup[] = [];
    days.forEach((day, index) => {
      const date = new Date(day);
      const preparedDay = {
        date: day,
        totalIndex: index,
      };
      let existingWeekGroup = weekGroups.find(
        (week) => getISOWeek(date) === week.isoWeek,
      );
      if (!existingWeekGroup) {
        existingWeekGroup = {
          isoWeek: getISOWeek(date),
          days: [],
        };
        weekGroups.push(existingWeekGroup);
      }
      existingWeekGroup.days.push(preparedDay);
    });
    return weekGroups;
  }

  prepareUserForList(user: PoolerUser): AvailabilityListUser {
    let availabilityListUser = user as AvailabilityListUser;
    if (!user.availabilities) {
      user.availabilities = [];
    }
    availabilityListUser.availabilities =
      this.sliceIrrelevantUserAvailabilities(
        user.availabilities,
        user.restWarnings || [],
      );
    availabilityListUser.totalDepartmentAvailableCount =
      this.getTotalAvailabilityCount(user, AvailabilityStatusEnum.AVAILABLE);
    availabilityListUser.availabilities = this.addMissingUserAvailabilities(
      user.availabilities,
    );
    availabilityListUser.totalWorkingCount = this.getTotalWorkingCount(user);
    availabilityListUser.detailsCollapsed = true;
    return availabilityListUser as AvailabilityListUser;
  }

  getTotalAvailabilityCount(
    user: PoolerUser,
    availabilityStatus: AvailabilityStatusEnum,
  ) {
    let count = 0;

    for (let availability of user.availabilities) {
      if (availability.isAvailable && availability.shifts) {
        const available = Object.values(availability.shifts).filter(
          (value) => value === availabilityStatus,
        );
        count += available.length;
      }
    }
    return count;
  }

  getTotalWorkingCount(user: PoolerUser): number {
    return user.availabilities.filter(
      (availability) => availability.bookedJobs !== undefined,
    ).length;
  }

  sliceIrrelevantUserAvailabilities(
    availabilities: UserAvailability[],
    restWarnings: UserRestWarning[],
  ): UserAvailability[] {
    if (!availabilities?.length) {
      return [];
    }
    availabilities = availabilities.sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    let startUnixTime = this.days[0];
    let endUnixTime = this.days[this.days.length - 1];

    let firstRelevantDateIndex = -1;
    let lastRelevantDateIndex = -1;

    // TODO: Discuss if this is wanted at all or should be removed
    let restWarningDates = restWarnings
      .filter(
        (restWarning) =>
          restWarning.type === UserRestWarningTypeEnum.SEVEN_DAY_WARNING ||
          (restWarning.type ===
            UserRestWarningTypeEnum.POTENTIAL_SEVEN_DAY_WARNING &&
            restWarning.withBreakInfraction),
      )
      .flatMap((restWarning) =>
        restWarning.type === UserRestWarningTypeEnum.SEVEN_DAY_WARNING
          ? restWarning.affectedDates.slice(
              0,
              restWarning.affectedDates.length - 1,
            )
          : restWarning.affectedDates,
      );

    for (let i = 0; i < availabilities.length; i++) {
      const currentAvailabilityUnix = availabilities[i].date;
      if (
        firstRelevantDateIndex === -1 &&
        currentAvailabilityUnix >= startUnixTime
      ) {
        firstRelevantDateIndex = i;
      }
      if (
        lastRelevantDateIndex === -1 &&
        currentAvailabilityUnix > endUnixTime
      ) {
        lastRelevantDateIndex = i - 1;
        break;
      }
      // reached end of loop without going over relevant date
      if (i == availabilities.length - 1) {
        lastRelevantDateIndex = i;
      }
      if (restWarningDates.some((date) => date === availabilities[i].date)) {
        availabilities[i].isAvailable = false;
        if (
          availabilities[i].shifts?.[ShiftEnum.EARLY] ===
          AvailabilityStatusEnum.AVAILABLE
        ) {
          availabilities[i].shifts![ShiftEnum.EARLY] =
            AvailabilityStatusEnum.UNAVAILABLE;
        }
        if (
          availabilities[i].shifts?.[ShiftEnum.LATE] ===
          AvailabilityStatusEnum.AVAILABLE
        ) {
          availabilities[i].shifts![ShiftEnum.LATE] =
            AvailabilityStatusEnum.UNAVAILABLE;
        }
        if (
          availabilities[i].shifts?.[ShiftEnum.DAY] ===
          AvailabilityStatusEnum.AVAILABLE
        ) {
          availabilities[i].shifts![ShiftEnum.DAY] =
            AvailabilityStatusEnum.UNAVAILABLE;
        }
        if (
          availabilities[i].shifts?.[ShiftEnum.NIGHT] ===
          AvailabilityStatusEnum.AVAILABLE
        ) {
          availabilities[i].shifts![ShiftEnum.NIGHT] =
            AvailabilityStatusEnum.UNAVAILABLE;
        }
      }
    }
    if (firstRelevantDateIndex === -1 || lastRelevantDateIndex === -1) {
      return [];
    }
    availabilities = availabilities.slice(
      firstRelevantDateIndex,
      lastRelevantDateIndex + 1,
    );
    return availabilities;
  }

  addMissingUserAvailabilities(
    availabilities: UserAvailability[],
  ): UserAvailability[] {
    // expects sorted array (sorted in previous function already)
    if (!availabilities.length) {
      return this.days.map((day) => {
        return {
          date: format(new Date(day), dbDayDateToken),
        };
      });
    }

    const availabilitiesCopy = [...availabilities];

    for (let i = 0; i < this.days.length; i++) {
      const loopDay = this.days[i];
      if (!availabilitiesCopy.length) {
        availabilities.push({
          date: loopDay,
        });
      } else {
        const nextAvailabilityString = availabilitiesCopy[0].date;
        if (loopDay !== nextAvailabilityString) {
          availabilities.push({
            date: loopDay,
          });
        } else {
          availabilitiesCopy.splice(0, 1);
        }
      }
    }

    // sort since new items were pushed to end of array
    availabilities = availabilities.sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    return availabilities;
  }
}
